/**
 * 화면 목록 — 사이드바·헤더·라우터가 **같은 표**를 본다.
 *
 * 이름과 설명이 세 곳에 흩어지면 반드시 어긋난다(사이드바는 "화면", 헤더는
 * "디스플레이" 같은 식으로). 한 곳에 두고 셋이 읽어 가게 한다.
 */
import {
  Activity,
  BarChart3,
  CalendarDays,
  LayoutDashboard,
  ListMusic,
  Megaphone,
  MapPin,
  Monitor,
  Server,
  FileText,
  Users,
  PackageOpen,
  LayoutGrid,
  Map as MapIcon,
  BookOpen,
  ClipboardCheck,
  History,
  LifeBuoy,
  Plug,
  Download,
  HardDriveDownload,
  Siren,
  Settings,
  SlidersHorizontal,
  Puzzle,
  Network,
  Boxes,
  type LucideIcon,
} from 'lucide-react';
import { DOCS } from './docs/registry.js';
import { MANUAL_CHAPTERS } from './manual/index.js';

export interface NavEntry {
  path: string;
  label: string;
  /** 헤더 부제. 그 화면에서 무엇을 하는지 한 줄로 말한다. */
  subtitle: string;
  icon: LucideIcon;
  /**
   * 이 능력이 없으면 사이드바에 나오지 않는다.
   *
   * 감추는 것은 편의이지 방어가 아니다 — 서버가 같은 권한을 다시 검사한다.
   * 다만 누를 수 없는 메뉴를 늘어놓으면 사용자는 자기가 뭘 할 수 있는지 모른다.
   */
  capability?: string;
  /**
   * 이 능력은 **사이드바에서 감추기 위한 것**이지 주소를 막는 것이 아니다.
   *
   * API 정의·시스템 문서가 그렇다 — 읽어서 해로울 것이 없는 계약과 설계 문서이고(호출을
   * 대신 보내 주지도 않는다), 다만 현장 운영자의 하루에 나올 자리가 아니라 메뉴에서만
   * 뺀다. 주소를 아는 사람은 그냥 열면 된다.
   *
   * **고객에게 보이면 안 되는 화면에는 쓰지 않는다.** 그런 자리는 `capability` 만 두어
   * 주소로도 막는다(시스템·장애 감시·플러그인·서버 설치 파일·출하 검사·에이전트).
   */
  menuOnly?: boolean;
}

export interface NavSection {
  title: string;
  items: NavEntry[];
}

/**
 * 하위 메뉴를 가진 메뉴. 머리 줄은 링크가 아니라 여닫는 손잡이다.
 */
export interface NavGroup {
  label: string;
  icon: LucideIcon;
  /** 이 능력이 없으면 머리 줄째로 사이드바에 나오지 않는다. */
  capability: string;
  items: readonly NavEntry[];
}

/**
 * 묶음은 실제 작업 순서를 따른다: 상태를 보고(MAIN), 무엇을 언제 틀지 정하고(운영),
 * 결과와 시스템을 확인한다(관리).
 *
 * 에이전트는 여기 없다 — 하단의 **정비 모드** 아래로 옮겼다(`SYSTEM_ADMIN_NAV`).
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    title: 'MAIN',
    items: [
      { path: '/', label: '대시보드', subtitle: '운영 현황 요약', icon: LayoutDashboard },
      { path: '/content', label: '콘텐츠', subtitle: '이미지·영상·템플릿·웹·HTML 콘텐츠 관리', icon: FileText },
      { path: '/displays', label: '화면', subtitle: '등록된 화면과 상태 관리', icon: Monitor },
      /*
       * 화면 그룹 — **화면 바로 아래.** 그룹은 화면 묶음이고, 그룹 편성·캠페인 대상·담당 범위가 모두 그것을 쓴다.
       * 2026-10-19 에는 [화면] 의 [⋯] › 그룹 관리 창 안에 있어 그룹이 있는지조차 메뉴에서 보이지 않았다(2026-10-21 뗐다).
       * 누구나 본다 — 고치기·만들기 단추는 화면이 권한을 보고 낸다(`screens/Groups.tsx`).
       */
      { path: '/groups', label: '화면 그룹', subtitle: '화면 묶음 — 그룹 편성·캠페인 대상·담당 범위', icon: Boxes },
      /*
       * 사업장·위치 — 화면을 지역 › 사업장 › 위치로 정리한다(2026-10-21). 지역·사업장은 모니터링의 [사업장 구성] 창,
       * 위치 이름 바꾸기·지우기는 화면 목록 [⋯] 의 [위치 관리] 창에 흩어져 있었다. 화면과 화면 그룹 바로 아래 — 둘 다
       * 화면을 묶는 다른 축이다. 누구나 본다 — 고치기는 화면이 권한을 보고 낸다(`screens/Locations.tsx`).
       *
       * 부제의 `{region}`·`{site}`·`{location}` 은 고객사가 고른 층 이름으로 채운다(`AdminLayout`, `siteLabels.ts`) —
       * 메뉴 이름은 그대로 둔다(층 이름을 무엇으로 고르든 그 층이 "사업장·위치" 다).
       */
      { path: '/locations', label: '사업장·위치', subtitle: '{region} › {site} › {location} — 화면을 둔 곳 정리', icon: MapPin },
      /*
       * 에이전트 내려받기 — **화면(과 화면 그룹) 바로 아래가 자리다.**
       *
       * 기기를 늘리는 일은 "프로그램을 깔고 → 뜬 코드를 화면 추가에 넣는" 한 흐름이다.
       * 그 첫 단계를 다른 묶음(정비 모드)에 두면 고객사 담당자는 찾지 못하고 공급사에
       * 전화한다. 실제로 `/agent` 는 `system` 이라 고객에게는 보이지도 않았다.
       *
       * **받는 것만 있는 화면이다.** 롤아웃·산출물 등록·설치 토큰은 `/agent`(공급사)에
       * 그대로 있다 — 되돌릴 수 없는 조작을 기기 늘리는 자리에 섞지 않는다.
       */
      {
        path: '/agent/download',
        label: '에이전트 설치',
        subtitle: '에이전트 설치 파일 다운로드',
        icon: Download,
        capability: 'fleet:write',
      },
      {
        path: '/wall',
        label: '모니터링',
        subtitle: '{region} › {site}별 가동 현황',
        icon: LayoutGrid,
      },
      /*
       * 통합 모니터링 — 이 서버와 통합 관제에 등록한 원격 서버의 화면을 한 지도에(2026-10-28 사용자 지시로 모니터링의
       * 전환에서 메뉴를 나눴다 — "통합 관리 서버가 적용되어 있으면 관리자 권한의 계정에도 통합 모니터링 메뉴가 보여야 한다").
       * 통합 관제 모듈이 켜져 있고 원격 서버가 하나라도 있을 때, 화면 관리 권한(관리자)에게 보인다(`features.hub_monitor`).
       * 통합 관제(서버 등록·원격 명령)는 따로 — 모듈을 고객사에 공개했을 때만 관리자에게 보인다.
       */
      {
        path: '/integrated',
        label: '통합 모니터링',
        subtitle: '연동된 서버까지 한 지도에',
        icon: MapIcon,
        capability: 'feature:hub_monitor',
      },
      /*
       * 통합 관제 — **이 서버가 다른 서버들을 모아 보는 자리.** 모니터링 바로 아래가 자리다: 이 서버의 화면을
       * 보고 나면 다음으로 보는 것이 다른 현장의 화면이다.
       *
       * 능력 대신 **기능 표식**(`feature:hub`)으로 가린다. 모듈(통합 관제)이 켜져 있고 이 사람이 볼 수 있는지는
       * 서버가 `/me` 의 `features.hub` 로 알려 준다(`navCapabilities`). 모아 보는 서버는 한두 대뿐이라, 모일 쪽
       * 서버의 사이드바에 빈 메뉴를 늘어놓지 않는다. 주소로도 막는다 — 서버도 404·409 로 막는다.
       */
      {
        path: '/hub',
        label: '통합 관제',
        subtitle: '여러 서버의 화면 현황과 원격 명령',
        icon: Network,
        capability: 'feature:hub',
      },
    ],
  },
  {
    title: '운영',
    items: [
      { path: '/playlists', label: '플레이리스트', subtitle: '콘텐츠 재생 순서 구성', icon: ListMusic },
      { path: '/schedules', label: '스케줄', subtitle: '요일·시간대별 편성', icon: CalendarDays },
      { path: '/campaigns', label: '캠페인', subtitle: '기간·대상 지정 편성', icon: Megaphone },
    ],
  },
  {
    title: '관리',
    items: [
      {
        path: '/reports',
        label: '리포트',
        subtitle: '재생 실적 집계',
        icon: BarChart3,
        capability: 'report:read',
      },
      /*
       * 활동 로그는 **시스템(공급사) 계정의 자리다**(2026-10-21 사용자 지시) — 메뉴도, 주소도.
       *
       * 그 전에는 감사로그를 볼 수 있는 사람(`audit:read` — 감사·고객사 관리자)에게 냈다. 이제 고객사 계정이 보는
       * 변경 기록은 대시보드의 "최근 활동" 카드와 콘텐츠·화면 상세의 "최근 변경" 뿐이다(그쪽은 그대로 `audit:read`).
       * 서버의 감사로그 API 권한은 바꾸지 않았다 — 그 카드들이 쓴다.
       */
      {
        path: '/activities',
        label: '활동 로그',
        subtitle: '변경 이력 조회',
        icon: Activity,
        capability: 'system',
      },
      {
        path: '/users',
        label: '사용자',
        subtitle: '계정과 역할 관리',
        icon: Users,
        /*
         * 운영자도 자기 팀의 계정을 만들고 비밀번호를 재설정한다. 그것 때문에 릴리스·
         * 서비스 토큰·서버 설정까지 넘겨줄 수는 없어 `admin` 에서 떼어 낸 능력이다.
         * **운영자에게 관리자 계정은 보이지 않는다** — 서버가 목록에서 빼고, 지목해도
         * 없는 사람으로 답한다.
         */
        capability: 'user:manage',
      },
      /*
       * 환경 설정 — 고객사가 자기 화면을 어떻게 보일지 정하는 자리(브랜드·대기 화면·운영 시간·사내
       * 주소 허용 목록). 서버 설정이 아니다 — 그것은 배포가 정하고 콘솔에서 바꾸지 않는다(시스템 화면).
       * 내용은 누구나 읽어도 되지만 매일 여는 자리가 아니라, 고칠 수 있는 사람의 메뉴에만 둔다.
       */
      {
        path: '/settings',
        label: '환경 설정',
        subtitle: '브랜드·대기 화면·운영 시간·사내 주소',
        icon: SlidersHorizontal,
        capability: 'settings:write',
      },
      /*
       * 시스템 화면은 **주소로도 막는다.**
       *
       * 한때 `menuOnly` 였다 — 사이드바에서만 감추고 주소는 열어 두었다. 안의 "내 권한" 이
       * 권한 없는 사람일수록 봐야 하는 것이라는 이유였는데, 고객사에 넘기는 콘솔에서는
       * 그 이유보다 **서버 상태·연결·편성 캐시가 공급사의 지표라는 사실**이 크다. 고객이
       * 주소를 알아내 여는 화면이 되면, 우리 쪽 운영 사정을 고객이 먼저 보고 묻게 된다.
       */
      {
        path: '/system',
        label: '시스템',
        subtitle: '서버 상태와 연결 정보',
        icon: Server,
        capability: 'system',
      },
    ],
  },
];

/**
 * API 정의 — 정비 모드 아래, **시스템 문서 바로 위**.
 *
 * 시스템 문서와 같은 성격이다. 매일 쓰는 화면이 아니라 **필요할 때 찾는 것**이라
 * 위쪽 작업 묶음에 섞지 않는다. 둘을 나란히 둔 이유는 찾는 사람이 같기 때문이다 —
 * 연동을 붙이는 사람은 계약을 먼저 보고 그다음에 설계 문서를 편다. 그 순서대로 놓았다.
 */
export const API_NAV: NavEntry = {
  path: '/api-spec',
  label: 'API 정의',
  subtitle: '외부 연동 엔드포인트와 권한',
  icon: Plug,
  /*
   * **연동을 붙이는 사람의 자리다.** 서비스 토큰을 발급하고 서버 설정을 다루는 사람이
   * 읽는 계약이고, 현장 운영자의 하루에는 나오지 않는다. 문서 자체는 누구나 읽어도 되는
   * 내용이라(호출을 대신 보내지도 않는다) 주소는 열어 두고 메뉴에서만 감춘다.
   */
  capability: 'system',
  menuOnly: true,
};

/**
 * 사용 설명서 — 사이드바 하단 고정 자리의 **유일한 메뉴**.
 *
 * API 정의·시스템 문서가 연동 개발자와 인수인계받는 사람의 것이라면(정비 모드 아래),
 * 이것은 매일 콘솔을 쓰는 현장 담당자의 것이다. 스크롤과 상관없이 늘 같은 자리에 둔다.
 * 능력을 가리지 않는다 — 권한이 없는 메뉴도 "왜 나한테는 없나" 를 여기서 읽어야 한다.
 */
export const MANUAL_NAV: NavEntry = {
  path: '/manual',
  label: '사용 설명서',
  subtitle: '메뉴별 기능과 에이전트 사용법',
  icon: LifeBuoy,
};

/**
 * 서버 설치 파일 — 정비 모드 아래 **첫 항목, 출하 검사 위**.
 *
 * 고객사 서버에 USB 로 가져갈 설치 패키지를 내려받는 자리다. 출하 검사와 같이 납품하는 쪽의
 * 메뉴이고, 순서도 납품 순서다 — 서버를 깔고 나서 검사한다.
 *
 * `menuOnly` 가 아니다 — 패키지는 제품 자체라 **주소를 알아도 관리자가 아니면 열리지 않는다**
 * (서버도 목록·내려받기에 `admin` 을 요구한다).
 */
export const SERVER_PACKAGES_NAV: NavEntry = {
  path: '/server-packages',
  label: '서버 설치 파일',
  subtitle: 'USB 설치용 서버 패키지',
  icon: HardDriveDownload,
  capability: 'system',
};

/**
 * 출하 검사 — 정비 모드 아래, **서버 설치 파일과 API 정의 사이**.
 *
 * 납품하는 쪽의 자리다. 시스템을 고객에게 넘기기 전에 한 번 열고, 넘긴 뒤에는 열 일이
 * 없다 — 매일 쓰는 묶음에 섞으면 현장 운영자의 메뉴만 한 칸 밀린다.
 *
 * `menuOnly` 가 아니다 — 고객사·검사자 이름이 적히는 문서라 **주소를 알아도 관리자가
 * 아니면 열리지 않는다.** API 정의·시스템 문서(누가 읽어도 되는 내용)와 다른 점이다.
 */
export const INSPECTION_NAV: NavEntry = {
  path: '/inspection',
  label: '출하 검사',
  subtitle: '납품 전 점검과 성적서 인쇄',
  icon: ClipboardCheck,
  capability: 'system',
};

/**
 * 납품 이력 — 정비 모드 아래, **출하 검사 바로 아래**(2026-10-29). 납품 순서 그대로 — 설치 파일 → 검사 → 납품 기록.
 *
 * 어느 판을 언제 어느 고객사에 납품했나(`@signageone/core` 의 `DELIVERIES`, 기기 정비 화면 [납품 기록] 과 같은 목록).
 * **다른 고객사 이름이 적히는 목록이라** 주소로도 막는다(`menuOnly` 아님). 이름을 기기 쪽([납품 기록])과 달리 둔 것은
 * 공급사 메뉴 이름이 고객 설명서에 나오지 않게 하는 규칙 때문이다 — 기기 정비 화면은 고객사 현장도 쓴다.
 */
export const DELIVERIES_NAV: NavEntry = {
  path: '/deliveries',
  label: '납품 이력',
  subtitle: '고객사별 납품 날짜와 서버·에이전트 판',
  icon: History,
  capability: 'system',
};

/**
 * 시스템 문서 — 정비 모드 아래. 문서 목록을 펼치는 하위 목록을 따로 갖는다.
 *
 * `NAV_SECTIONS` 에 넣지 않은 이유가 있다. 위쪽 묶음은 매일 쓰는 화면이고 이것은
 * 필요할 때 찾는 것이다. 같은 흐름에 섞으면 매일 쓰는 메뉴가 그만큼 아래로 밀린다.
 */
export const DOCS_NAV: NavEntry = {
  path: '/docs',
  label: '시스템 문서',
  subtitle: '기획·설계 문서',
  icon: BookOpen,
  /* 설계·운영 문서도 같은 자리다 — 인수인계받는 사람이 찾아 읽는 것이지 매일 쓰는 메뉴가 아니다. */
  capability: 'system',
  menuOnly: true,
};

/**
 * 에이전트 — 화면 기기에 까는 프로그램을 내려받고 버전을 배포하는 자리.
 *
 * 한때 '관리' 묶음에 있었다. 매일 쓰는 메뉴가 아니라 서비스를 운영하는 사람이 가끔 여는
 * 자리라 정비 모드 아래로 옮겼다. `menuOnly` 가 아니다 — 주소로도 관리자만 연다.
 */
export const AGENT_NAV: NavEntry = {
  path: '/agent',
  label: '에이전트',
  subtitle: '설치 명령·롤아웃·산출물 관리',
  icon: PackageOpen,
  capability: 'system',
};

/**
 * 장애 감시 — 서버가 스스로 점검해 남긴 장애 기록과 알림 발송 결과.
 *
 * 시스템 역할의 자리다(서버도 `admin` 을 요구한다). 알림 채널 설정은 여기 없다 — 비밀을 담는 설정이라
 * 플러그인(`PLUGINS_NAV`)에 둔다. `menuOnly` 가 아니다(주소로도 막는다).
 */
export const ALERTS_NAV: NavEntry = {
  path: '/alerts',
  label: '장애 감시',
  subtitle: '서버 자동 점검 기록',
  icon: Siren,
  capability: 'system',
};

/**
 * 플러그인 — **모듈을 어느 단계로 어디까지 내보낼지 정하는 개발자 전용 화면.** 시스템 역할.
 *
 * 모듈마다 넷을 정한다: 사용·해제 / 베타·정식 / 고객사 공개 / 패키지 기본값. 그 아래에 모듈의 설정
 * (외부 저장소 경로·메일 서버 비밀번호·웹훅 주소·SMS 키)이 있다. 납품 뒤 고객사 관리자(admin)가
 * 아니라 공급사가 맞추는 자리다. 서버가 요구하는 능력(`plugin:manage`)으로 가린다 — 시스템 역할만
 * 갖는다. 주소로도 막는다.
 *
 * 고객사에 **공개한** 모듈의 상태만 관리자 화면(시스템·장애 감시)에 나타난다(`/api/v1/plugins/modules`).
 */
export const PLUGINS_NAV: NavEntry = {
  path: '/plugins',
  label: '플러그인',
  subtitle: '모듈 단계·공개 범위·패키지 기본값',
  icon: Puzzle,
  capability: 'plugin:manage',
};

/**
 * 정비 모드 — 사이드바 스크롤 영역의 **맨 끝(관리 묶음 아래)** 에 붙는 여닫는 묶음.
 *
 * 납품하고(서버 설치 파일·출하 검사), 연동을 붙이고(API 정의), 인수인계받고(시스템 문서),
 * 기기에 프로그램을 까는(에이전트) 자리를 한데 모았다. 모두 현장 운영자의 하루에는 나오지
 * 않는 메뉴라, 하나씩 늘어놓으면 관리자의 사이드바만 길어진다.
 *
 * **시스템 역할(`admin`)에게만 낸다.** 묶음째로 감추므로 하위 항목도 `admin` 이거나, `admin` 을 가진
 * 역할만 갖는 능력(플러그인의 `plugin:manage`)이어야 한다(시험이 본다).
 * 주소를 막는지는 항목마다 다르다 — API 정의·시스템 문서는 `menuOnly` 라 주소로는 열린다.
 */
export const SYSTEM_ADMIN_NAV: NavGroup = {
  label: '정비 모드',
  icon: Settings,
  capability: 'system',
  items: [ALERTS_NAV, PLUGINS_NAV, SERVER_PACKAGES_NAV, INSPECTION_NAV, DELIVERIES_NAV, API_NAV, DOCS_NAV, AGENT_NAV],
};

/** 능력에 맞는 정비 모드 하위 메뉴. 비면 머리 줄도 내지 않는다. */
export function visibleSystemAdmin(capabilities: readonly string[]): readonly NavEntry[] {
  if (!capabilities.includes(SYSTEM_ADMIN_NAV.capability)) return [];
  return SYSTEM_ADMIN_NAV.items.filter((item) => !item.capability || capabilities.includes(item.capability));
}

const ALL = [...NAV_SECTIONS.flatMap((section) => section.items), ...SYSTEM_ADMIN_NAV.items];

/** 사이드바에 없지만 주소가 있는 화면. 헤더 제목이 비지 않게 한다. */
const HIDDEN: ReadonlyArray<{ path: string; label: string; subtitle: string }> = [
  { path: '/account', label: '내 계정', subtitle: '비밀번호와 로그인 기기 관리' },
  // 사용 설명서 첫 화면의 [화면으로 보기] — 소개서를 슬라이드째 세로로 읽는다.
  { path: '/overview', label: '시스템 소개', subtitle: '도입을 검토하는 분께 보여 드리는 소개 슬라이드' },
  // 서버 설치 파일 메뉴의 두 번째 탭 — 메뉴는 부모(SERVER_PACKAGES_NAV)가 켠다.
  {
    path: '/server-packages/manual',
    label: '서버 설치 매뉴얼',
    subtitle: '설치 절차와 관리자 명령',
  },
];

/**
 * 이 주소를 열 수 있는가 — **사이드바와 같은 표를 본다.**
 *
 * 라우터에 권한을 따로 적어 두었더니 메뉴와 조용히 어긋났다: 메뉴에서는 감춰졌는데 주소를
 * 치면 열리는 화면이 생긴다. 한쪽을 고칠 때 다른 쪽을 함께 고치리라고 기대할 수 없으므로,
 * 둘이 같은 값을 읽게 한다.
 *
 * 표에 없는 주소(내 계정 등)와 `menuOnly` 항목(API 정의 등)은 능력을 가리지 않는다.
 */
export function canOpen(pathname: string, capabilities: readonly string[]): boolean {
  const entry = ALL.find((item) => item.path === pathname);
  if (!entry?.capability || entry.menuOnly) return true;
  return capabilities.includes(entry.capability);
}

/**
 * 메뉴를 가리는 데 쓰는 목록 — 서버의 능력에 **기능 표식**(`feature:<id>`)을 더한다.
 *
 * 기능 표식은 서버 능력이 아니다. 모듈을 켰는지·이 사람이 볼 수 있는지처럼 역할만으로 정해지지 않는 것을
 * 서버가 `/me` 의 `features` 로 알려 주고, 메뉴와 주소 막기(`canOpen`)가 능력과 같은 길로 읽게 붙인다.
 * 화면 안의 단추를 가리는 데는 쓰지 않는다 — 그것은 `me.capabilities` 를 그대로 본다.
 */
export function navCapabilities(me: { capabilities: readonly string[]; features?: Readonly<Record<string, boolean | undefined>> }): string[] {
  const features = Object.entries(me.features ?? {})
    .filter(([, on]) => on === true)
    .map(([id]) => `feature:${id}`);
  return [...me.capabilities, ...features];
}

/** 능력에 맞는 항목만 남긴 사이드바. */
export function visibleSections(capabilities: readonly string[]): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.capability || capabilities.includes(item.capability)),
  })).filter((section) => section.items.length > 0);
}

/** 상세 화면처럼 사이드바에 없는 경로의 제목. */
const DETAIL_TITLES: ReadonlyArray<{ prefix: string; label: string; subtitle: string }> = [
  { prefix: '/content/', label: '콘텐츠 상세', subtitle: '콘텐츠 내용과 배포 현황' },
  { prefix: '/displays/', label: '화면 상세', subtitle: '현재 송출 내용과 설정' },
];

export function titleFor(pathname: string): { label: string; subtitle: string } {
  const exact = [...ALL, ...HIDDEN, MANUAL_NAV].find((entry) => entry.path === pathname);
  if (exact) return { label: exact.label, subtitle: exact.subtitle };

  // 설명서의 장도 문서처럼 제목을 목차에서 가져온다.
  const chapter = MANUAL_CHAPTERS.find((entry) => pathname === `/manual/${entry.slug}`);
  if (chapter) return { label: chapter.title, subtitle: chapter.summary };

  /*
   * 문서는 제목이 표가 아니라 문서 목록에서 온다. 문서를 하나 늘릴 때마다 이 파일도
   * 고쳐야 한다면 반드시 한쪽을 빠뜨리고, 그러면 헤더만 'SignageOne' 으로 남는다.
   */
  const doc = DOCS.find((entry) => pathname === `/docs/${entry.slug}`);
  if (doc) return { label: doc.title, subtitle: doc.summary };

  const detail = DETAIL_TITLES.find((entry) => pathname.startsWith(entry.prefix));
  if (detail) return { label: detail.label, subtitle: detail.subtitle };

  return { label: 'SignageOne', subtitle: '' };
}
